import React, { useState } from 'react';
import { CaptureReturnDetails, SkuOption } from '../driverReturnsTypes';
import styles from './CaptureReturnForm.module.css';

interface CaptureReturnFormProps {
  details: CaptureReturnDetails;
  skuOptions: SkuOption[];
  onSubmit?: (lines: ReturnLine[]) => void | Promise<void>;
  onCancel?: () => void;
  submitting?: boolean;
}

export interface ReturnLine {
  id: string;
  sku: string;
  quantity: string;
  condition: string;
  notes: string;
}

const newLine = (): ReturnLine => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  sku: '',
  quantity: '',
  condition: 'Good',
  notes: '',
});

export const CaptureReturnForm: React.FC<CaptureReturnFormProps> = ({
  details,
  skuOptions,
  onSubmit,
  onCancel,
  submitting,
}) => {
  const [lines, setLines] = useState<ReturnLine[]>([newLine()]);

  const updateLine = (id: string, key: keyof ReturnLine, value: string) => {
    setLines((prev) => prev.map((l) => (l.id === id ? { ...l, [key]: value } : l)));
  };

  const valid = lines.some((l) => l.sku && Number(l.quantity) > 0);

  const handleConfirm = async () => {
    if (!valid) return;
    const filled = lines.filter((l) => l.sku && Number(l.quantity) > 0);
    await onSubmit?.(filled);
    setLines([newLine()]);
  };

  return (
    <div className={styles.card}>
      <h3 className={styles.title}>Capture Return - {details.returnId}</h3>

      <div className={styles.headerRow}>
        <div className={styles.headerBox}>
          <p className={styles.headerLabel}>Order #</p>
          <p className={styles.headerValue}>{details.orderId}</p>
        </div>
        <div className={styles.headerBox}>
          <p className={styles.headerLabel}>Customer</p>
          <p className={styles.headerValue}>{details.customer}</p>
        </div>
      </div>

      <div className={styles.lines}>
        {lines.map((line) => (
          <div key={line.id} className={styles.lineCard}>
            <div className={styles.gridRow}>
              <div className={styles.field}>
                <label className={styles.label}>SKU</label>
                <select
                  className={styles.select}
                  value={line.sku}
                  onChange={(e) => updateLine(line.id, 'sku', e.target.value)}
                >
                  <option value="">Select...</option>
                  {skuOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Quantity</label>
                <input
                  className={styles.input}
                  value={line.quantity}
                  onChange={(e) => updateLine(line.id, 'quantity', e.target.value)}
                />
              </div>
              <div className={styles.field}>
                <label className={styles.label}>Condition</label>
                <select
                  className={styles.select}
                  value={line.condition}
                  onChange={(e) => updateLine(line.id, 'condition', e.target.value)}
                >
                  <option>Good</option>
                  <option>Damaged</option>
                  <option>Expired</option>
                </select>
              </div>
            </div>
            <div className={styles.field}>
              <label className={styles.label}>Notes</label>
              <input
                className={styles.input}
                value={line.notes}
                onChange={(e) => updateLine(line.id, 'notes', e.target.value)}
              />
            </div>
          </div>
        ))}
      </div>

      <button
        type="button"
        className={styles.addBtn}
        onClick={() => setLines((prev) => [...prev, newLine()])}
      >
        + Add Another Item
      </button>

      <div className={styles.actions}>
        <button
          className={styles.confirmBtn}
          disabled={!valid || submitting}
          onClick={handleConfirm}
        >
          {submitting ? 'Submitting…' : 'Confirm Return'}
        </button>
        <button className={styles.cancelBtn} onClick={() => onCancel?.()}>Cancel</button>
      </div>
    </div>
  );
};
